/*
 * Copyright (c) 2026. Bolmate B.V.
 *
 * Mobile push-subscription guard. On phones/tablets push is only allowed for
 * the INSTALLED PWA (the enable() install gate enforces that going forward),
 * but a subscription can still be left behind in the BROWSER context:
 * - Android shares one subscription between Chrome and the WebAPK, so after
 *   an uninstall the browser keeps receiving pushes for an app that no
 *   longer exists;
 * - legacy subscriptions created in mobile browsers before the install gate.
 *
 * The guard runs on every app load (app.tsx) and — additionally — whenever
 * the browser fires `beforeinstallprompt` (a definitive "not installed"
 * signal): if a subscription exists in a mobile browser while the PWA is not
 * installed on this device, it is unregistered and unsubscribed on the spot.
 * Install detection comes from the pwaInstallState service, never from the
 * install banners' visibility logic.
 */

import {ResponseData} from '@dvrd/dvr-controls';

import {unregisterDeviceToken} from './requestSender';
import {isMobileDevice, isStandalone} from './pwaUtil';
import {isPwaConfirmedNotInstalled, isPwaInstalledOnThisDevice} from './pwaInstallState';
import {
    getStandaloneSeen,
    removeOptedInPair as idbRemoveOptedInPair,
    setUserOptedIn as idbSetUserOptedIn
} from './pushIdb';
import {anyAccountOptedIn} from '../hooks/usePushSubscription';

// Same key layout as usePushSubscription's opt-in flags (also read by
// deviceList's current-device logout path).
const PUSH_OPTED_IN_PREFIX = 'bolmate-push-opted-in:';

/** Remove the current device's push subscription when it lives in a MOBILE
 *  BROWSER while the Bolmate PWA is NOT installed on this device. Returns
 *  true when a subscription was removed. Desktop browsers and the installed
 *  app itself are never touched. */
export async function cleanupUninstalledPwaSubscription(
    userId: string | null,
    accountId: string | null
): Promise<boolean> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    // Mobile only: a desktop browser subscription is a legitimate push target.
    // Hardware-derived, so a phone running with "desktop site" on is still
    // recognised — that is exactly where a browser subscription could be
    // minted while the UA-only check was in place.
    if (!isMobileDevice()) return false;
    // The installed app must never tear down its own subscription.
    if (isStandalone()) return false;
    try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        const sub = (await reg?.pushManager.getSubscription()) ?? null;
        if (!sub) return false;
        if (await isPwaInstalledOnThisDevice()) return false;
        // `standalone_seen` never expires, so on its own it would permanently
        // exempt every device that has ever run the installed app — which is
        // exactly the uninstall case this guard exists for. Only a positive
        // "not installed" verdict overrides it.
        if ((await getStandaloneSeen()) && !isPwaConfirmedNotInstalled()) return false;

        // Zombie confirmed: mobile browser + subscription + no installed app.
        // Backend row first (needs the endpoint before unsubscribe), then the
        // browser subscription, then the local opt-in state so nothing
        // silently re-registers the endpoint on a later refresh.
        if (userId && accountId) {
            await new Promise<void>(resolve => {
                unregisterDeviceToken({
                    data: {account_id: accountId, endpoint: sub.endpoint},
                    callback: (_data: ResponseData) => resolve(),
                    // Network failure / non-2xx never fires `callback` — resolve
                    // anyway so the browser-side unsubscribe below still runs
                    // (the backend row then dies via 404/410 or the cron).
                    errorCallback: () => resolve()
                });
            });
        }
        try {
            await sub.unsubscribe();
        } catch {
            /* best effort — the backend row is already gone */
        }
        if (userId && accountId) {
            try {
                localStorage.removeItem(`${PUSH_OPTED_IN_PREFIX}${userId}:${accountId}`);
            } catch {
                /* private browsing */
            }
            void idbRemoveOptedInPair(userId, accountId);
            // Per-user SW mirror: only true while some OTHER account of this
            // user still wants push here (same rule as disable()).
            void idbSetUserOptedIn(userId, anyAccountOptedIn(userId));
        }
        return true;
    } catch {
        return false;
    }
}
